import React, { useState } from 'react';
import './CodeEditor.css';

interface CodeEditorProps {
  initialCode: string;
  initialCss: string;
  appName: string;
  onSave: (code: string, css: string) => void;
  onClose: () => void;
}

const CodeEditor: React.FC<CodeEditorProps> = ({
  initialCode,
  initialCss,
  appName,
  onSave,
  onClose
}) => {
  const [activeTab, setActiveTab] = useState<'jsx' | 'css'>('jsx');
  const [code, setCode] = useState(initialCode);
  const [css, setCss] = useState(initialCss);
  const [hasChanges, setHasChanges] = useState(false);

  const handleCodeChange = (value: string) => {
    setCode(value);
    setHasChanges(true);
  };

  const handleCssChange = (value: string) => {
    setCss(value);
    setHasChanges(true);
  };

  const handleSave = () => {
    onSave(code, css);
    setHasChanges(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Cmd/Ctrl + S to save
    if ((e.metaKey || e.ctrlKey) && e.key === 's') {
      e.preventDefault();
      handleSave();
    }
  };

  return (
    <div className="code-editor" onKeyDown={handleKeyDown}>
      <div className="code-editor-tabs">
        <button
          className={`tab ${activeTab === 'jsx' ? 'active' : ''}`}
          onClick={() => setActiveTab('jsx')}
        >
          App.jsx
        </button>
        <button
          className={`tab ${activeTab === 'css' ? 'active' : ''}`}
          onClick={() => setActiveTab('css')}
        >
          styles.css
        </button>
        <div className="tab-spacer" />
        <button
          className="save-button"
          onClick={handleSave}
          disabled={!hasChanges}
        >
          {hasChanges ? '● Save' : 'Saved'}
        </button>
      </div>

      <div className="code-editor-content">
        {activeTab === 'jsx' ? (
          <textarea
            className="code-textarea"
            value={code}
            onChange={(e) => handleCodeChange(e.target.value)}
            spellCheck={false}
            placeholder="// Your React component code here..."
          />
        ) : (
          <textarea
            className="code-textarea"
            value={css}
            onChange={(e) => handleCssChange(e.target.value)}
            spellCheck={false}
            placeholder="/* Your CSS styles here */"
          />
        )}
      </div>

      <div className="code-editor-footer">
        <span className="hint">⌘S to save</span>
        <span className="file-info">{appName}</span>
      </div>
    </div>
  );
};

export default CodeEditor;
